import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProgressBar from "../../components/common/ProgressBar";
import OrderNotFound from "../../components/OrderNotFound";
import PremiumLoader from "../../components/PremiumLoader";
import { callGetWebservice, callPostWebservice } from "../../hooks/api";
import useLocationHook from "../../hooks/useLocation";
import "./AcceptedOrder.css";

const renderOptions = (optionsStr) => {
  if (!optionsStr) return null;
  try {
    const parsed = JSON.parse(optionsStr);
    if (typeof parsed === "object" && parsed !== null) {
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
    }
  } catch (e) {
    return optionsStr;
  }
  return optionsStr;
};

const AcceptedOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { latitude: userLat, longitude: userLng } = useLocationHook();

  const queryParams = new URLSearchParams(location.search);
  const tokenFromUrl = queryParams.get("token");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [merchantSettings] = useState(null);

  // Popup state
  const [showNamePopup, setShowNamePopup] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [tempCoords, setTempCoords] = useState({ lat: null, lng: null });

  useEffect(() => {
    if (tokenFromUrl) {
      const reqData = { type: "accepted-order", token: tokenFromUrl };
      callGetWebservice(
        "portal-order-details",
        reqData,
        (response) => {
          if (response.success) {
            const fetchedOrder = response.data.order;
            setOrder(fetchedOrder);
          } else {
            setError(response);
          }
          setLoading(false);
        },
        (error) => {
          setError(error.errMessage || "Error fetching order");
          setLoading(false);
        },
        null,
        dispatch,
        navigate,
      );
    } else {
      setLoading(false);
    }
  }, [tokenFromUrl, dispatch, navigate]);

  const handleReceivedOrder = () => {
    if (userLat && userLng) {
      setTempCoords({ lat: userLat, lng: userLng });
      setShowNamePopup(true);
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setTempCoords({ lat: latitude, lng: longitude });
            setShowNamePopup(true);
          },
          (error) => {
            console.warn("Geolocation error:", error);
            if (error.code === error.PERMISSION_DENIED) {
              alert(
                "Location access was denied. Please click the 'Lock' or 'Info' icon in your browser's address bar, allow location access, and try again.",
              );
            } else {
              alert(
                "Location access is mandatory to receive this order. Please enable location permissions in your browser and try again.",
              );
            }
          },
        );
      } else {
        alert(
          "Geolocation is not supported by your browser. Location access is mandatory to receive this order.",
        );
      }
    }
  };

  const submitAcceptOrder = (latitude, longitude, name) => {
    const postData = {
      order_slug: order?.slug,
      merchantId: order?.merchant_id,
      latitude,
      longitude,
      customer_name: name,
    };

    callPostWebservice(
      "accepted-order",
      postData,
      (response) => {
        setSubmitting(false);
        if (response.success) {
          navigate("/order-accepted-success", {
            state: { orderSlug: order?.slug },
          });
        } else {
          alert(response.message || "Failed to accept order");
        }
      },
      (error) => {
        setSubmitting(false);
        alert(error.errMessage || "Failed to accept order. Please try again.");
      },
      dispatch,
      navigate,
    );
  };

  if (loading) {
    return <PremiumLoader message="Loading order details..." />;
  }

  return (
    <div className="accepted-order-wrapper">
      {order ? (
        <div className="accepted-order-card">
          <div className="order-header">
            {merchantSettings?.logo && (
              <img
                src={merchantSettings.logo}
                alt="Merchant Logo"
                className="merchant-logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <h2 className="title">Order Details</h2>
            <div className="order-badges">
              <span className="badge badge-status">{order.current_status}</span>
              <span
                className={`badge badge-payment ${order.payment_status?.toLowerCase()}`}
              >
                {order.payment_status}
              </span>
            </div>
          </div>

          <div className="order-body">
            <div className="info-group">
              <div className="info-label">Order No</div>
              <div className="info-value font-weight-bold">{order.slug}</div>
            </div>

            {order.latitude && order.longitude && (
              <div className="info-group">
                <div className="info-label">Customer Location</div>
                <div className="info-value location-value">
                  {/* <span className="coords-text">Lat: {parseFloat(order.latitude).toFixed(4)}, Lng: {parseFloat(order.longitude).toFixed(4)}</span> */}
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${order.latitude},${order.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-direction"
                  >
                    <i className="fas fa-directions"></i> Get Directions
                  </a>
                </div>
              </div>
            )}

            {order.meta?.riderLocations && (
              <div className="info-group">
                <div className="info-label">Rider Location</div>
                <div className="info-value location-value">
                  {/* <span className="coords-text">Lat: {order.meta.riderLocations.latitude.toFixed(4)}, Lng: {order.meta.riderLocations.longitude.toFixed(4)}</span> */}
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${order.meta.riderLocations.latitude},${order.meta.riderLocations.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-direction"
                  >
                    <i className="fas fa-directions"></i> Get Directions
                  </a>
                </div>
              </div>
            )}

            <div className="items-section">
              <h4 className="section-title">Items ({order.total_quantity})</h4>
              <div className="items-list">
                {order.order_items?.map((item, idx) => (
                  <div key={idx} className="item-row">
                    <div className="item-image-wrapper">
                      {item.itemable?.image ? (
                        <img
                          src={item.itemable.image}
                          alt={item.itemable.name}
                          className="item-image"
                        />
                      ) : (
                        <div className="item-image-placeholder">No Image</div>
                      )}
                    </div>
                    <div className="item-details">
                      <div className="item-name">
                        {item.itemable?.name} x {item.quantity}
                      </div>
                      <div className="item-options">
                        {renderOptions(item.options)}
                      </div>
                    </div>
                    <div className="item-price">
                      {order.currency?.symbol}{" "}
                      {Number(item.sale_price).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="amount-breakdown">
              <div className="breakdown-row">
                <span>Subtotal</span>
                <span>
                  {order.currency?.symbol}{" "}
                  {Number(order.total_price).toLocaleString()}
                </span>
              </div>

              {order.total_shipment && Number(order.total_shipment) > 0 && (
                <div className="breakdown-row">
                  <span>Delivery Fee</span>
                  <span>
                    {order.currency?.symbol}{" "}
                    {Number(order.total_shipment).toLocaleString()}
                  </span>
                </div>
              )}

              {order.gst_val && Number(order.gst_val) > 0 && (
                <div className="breakdown-row">
                  <span>GST ({Number(order.gst_val)}%)</span>
                  <span>
                    {order.currency?.symbol}{" "}
                    {Number(order.gst).toLocaleString()}
                  </span>
                </div>
              )}

              {order.sales_tax_per && Number(order.sales_tax_per) > 0 && (
                <div className="breakdown-row">
                  <span>Sales Tax ({Number(order.sales_tax_per)}%)</span>
                  <span>
                    {order.currency?.symbol}{" "}
                    {Number(order.sales_tax).toLocaleString()}
                  </span>
                </div>
              )}

              {order.income_tax_per && Number(order.income_tax_per) > 0 && (
                <div className="breakdown-row">
                  <span>Income Tax ({Number(order.income_tax_per)}%)</span>
                  <span>
                    {order.currency?.symbol}{" "}
                    {Number(order.income_tax).toLocaleString()}
                  </span>
                </div>
              )}

              {order.tip_per && Number(order.tip_per) > 0 && (
                <div className="breakdown-row">
                  <span>Tip ({Number(order.tip_per)}%)</span>
                  <span>
                    {order.currency?.symbol}{" "}
                    {Number(order.tip).toLocaleString()}
                  </span>
                </div>
              )}

              {order.tip &&
                Number(order.tip) > 0 &&
                (!order.tip_per || Number(order.tip_per) === 0) && (
                  <div className="breakdown-row">
                    <span>Tip</span>
                    <span>
                      {order.currency?.symbol}{" "}
                      {Number(order.tip).toLocaleString()}
                    </span>
                  </div>
                )}

              {order.coupon_val && Number(order.coupon_val) > 0 && (
                <div className="breakdown-row discount">
                  <span>Discount</span>
                  <span>
                    - {order.currency?.symbol}{" "}
                    {Number(order.coupon_val).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="breakdown-row total">
                <span>Total Amount</span>
                <span>
                  {order.currency?.symbol}{" "}
                  {Number(order.total_amount).toLocaleString()}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn accept-button w-100 mt-4"
              onClick={handleReceivedOrder}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>{" "}
                  Accepting...
                </>
              ) : (
                <span className="d-flex align-items-center justify-content-center">
                  <i
                    className="fas fa-check-circle"
                    style={{ marginRight: "8px" }}
                  ></i>{" "}
                  Order Received
                </span>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="accepted-order-card">
          <OrderNotFound error={error} />
        </div>
      )}
      <ProgressBar submitting={submitting} message="Receiving order..." />

      {showNamePopup && (
        <div className="name-popup-overlay">
          <div className="name-popup-content">
            <h3 className="name-popup-title">Who is receiving this order?</h3>
            <p className="name-popup-desc">
              Please enter your name to confirm receipt.
            </p>
            <input
              type="text"
              className="form-control name-popup-input"
              placeholder="Your Full Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              autoFocus
            />
            <div className="name-popup-actions">
              <button
                className="btn name-popup-btn-cancel"
                onClick={() => {
                  setShowNamePopup(false);
                  setCustomerName("");
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn name-popup-btn-save"
                onClick={() => {
                  if (!customerName.trim()) {
                    alert("Please enter your name to continue.");
                    return;
                  }
                  setSubmitting(true);
                  submitAcceptOrder(
                    tempCoords.lat,
                    tempCoords.lng,
                    customerName,
                  );
                }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>{" "}
                    Saving...
                  </>
                ) : (
                  "Save & Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcceptedOrder;
